/** Freezes entities for a fixed number of frames on hit, producing the hitstop game-feel effect. */
import { Entity } from '../entities/Entity';
import { PhysicsComponent } from '../components/PhysicsComponent';

interface FrozenEntry {
  entity: Entity;
  framesLeft: number;
  savedVelocityX: number;
  savedVelocityY: number;
}

/** Tracks entities under hitstop and manages their freeze/restore lifecycle each frame. */
export class HitstopSystem {
  private frozen: FrozenEntry[] = [];

  /** Freeze an entity's velocity and apply a white tint for the given number of frames. */
  freeze(entity: Entity, frames: number): void {
    // Don't double-freeze
    if (this.isFrozen(entity)) return;

    const physics = entity.getComponent<PhysicsComponent>('physics');
    let vx = 0;
    let vy = 0;

    if (physics) {
      vx = physics.body.velocity.x;
      vy = physics.body.velocity.y;
      physics.body.setVelocity(0, 0);
    }

    // Visual feedback: brief white tint
    if (entity.sprite && 'setTintFill' in entity.sprite) {
      (entity.sprite as Phaser.GameObjects.Sprite).setTintFill(0xffffff);
    }

    this.frozen.push({
      entity,
      framesLeft: frames,
      savedVelocityX: vx,
      savedVelocityY: vy,
    });
  }

  /** Return true if the entity is currently hitstop-frozen. */
  isFrozen(entity: Entity): boolean {
    return this.frozen.some(e => e.entity === entity);
  }

  /** Decrement freeze counters and restore velocity and tint when timers expire. */
  update(): void {
    for (let i = this.frozen.length - 1; i >= 0; i--) {
      const entry = this.frozen[i];
      entry.framesLeft--;

      if (entry.framesLeft <= 0) {
        // Restore velocity
        const physics = entry.entity.getComponent<PhysicsComponent>('physics');
        if (physics) {
          physics.body.setVelocity(entry.savedVelocityX, entry.savedVelocityY);
        }

        // Clear tint
        if (entry.entity.sprite && 'clearTint' in entry.entity.sprite) {
          (entry.entity.sprite as Phaser.GameObjects.Sprite).clearTint();
        }

        this.frozen.splice(i, 1);
      }
    }
  }

  /** Immediately unfreeze all entities and clear the frozen list. */
  clear(): void {
    for (const entry of this.frozen) {
      if (entry.entity.sprite && 'clearTint' in entry.entity.sprite) {
        (entry.entity.sprite as Phaser.GameObjects.Sprite).clearTint();
      }
    }
    this.frozen = [];
  }
}
